/**
 * Error envelope — RFC 7807 `application/problem+json`.
 *
 * One shape for every failure in the system, so the client has exactly one
 * error path to handle. Traces: API-002.
 *
 * Security note: `detail` is shown to the user, so it must never carry
 * internal state — no SQL, no stack traces, no record contents the caller is
 * not authorized to see. Diagnostic context belongs in the structured log,
 * correlated by `requestId` (SEC-013, OBS-001).
 */

import { z } from 'zod';

export const ERROR_CODES = [
  'VALIDATION_FAILED',
  'UNAUTHENTICATED',
  'SESSION_EXPIRED',
  'FORBIDDEN',
  'NOT_FOUND',
  'CONFLICT',
  'INVALID_STATE_TRANSITION',
  'PRECONDITION_FAILED',
  'RATE_LIMITED',
  'PAYLOAD_TOO_LARGE',
  'UNSUPPORTED_FILE_TYPE',
  'FILE_NOT_SCANNED',
  'ACCOUNT_LOCKED',
  'ACCOUNT_DISABLED',
  'PASSWORD_CHANGE_REQUIRED',
  'INTERNAL_ERROR',
  'SERVICE_UNAVAILABLE',
] as const;

export const errorCodeSchema = z.enum(ERROR_CODES);
export type ErrorCode = z.infer<typeof errorCodeSchema>;

export const HTTP_STATUS_FOR_CODE: Record<ErrorCode, number> = {
  VALIDATION_FAILED: 422,
  UNAUTHENTICATED: 401,
  SESSION_EXPIRED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  CONFLICT: 409,
  INVALID_STATE_TRANSITION: 409,
  PRECONDITION_FAILED: 412,
  RATE_LIMITED: 429,
  PAYLOAD_TOO_LARGE: 413,
  UNSUPPORTED_FILE_TYPE: 415,
  FILE_NOT_SCANNED: 409,
  ACCOUNT_LOCKED: 423,
  ACCOUNT_DISABLED: 403,
  PASSWORD_CHANGE_REQUIRED: 403,
  INTERNAL_ERROR: 500,
  SERVICE_UNAVAILABLE: 503,
};

export interface FieldError {
  readonly field: string;
  readonly code: string;
  readonly message: string;
}

export interface ProblemDetails {
  /** Stable URI identifying the error class. */
  readonly type: string;
  /** Short human-readable summary. */
  readonly title: string;
  readonly status: number;
  readonly code: ErrorCode;
  /** Explanation for this occurrence. Never carries internal state. */
  readonly detail: string;
  /** Path of the request that failed. */
  readonly instance?: string;
  readonly errors?: readonly FieldError[];
  /** Correlates to the structured log entry holding full diagnostic context. */
  readonly requestId: string;
  /** Present on INVALID_STATE_TRANSITION — what the caller may do instead. */
  readonly availableActions?: readonly string[];
}

export const ERROR_TYPE_BASE = 'https://docs.lto-confirmation.gov.ph/errors';

export function problemType(code: ErrorCode): string {
  return `${ERROR_TYPE_BASE}/${code.toLowerCase().replace(/_/g, '-')}`;
}

/**
 * Success envelope. `meta` carries pagination and nothing else — it is not a
 * dumping ground for fields that belong in `data`.
 */
export interface ApiSuccess<T, M = undefined> {
  readonly data: T;
  readonly meta?: M;
}

/**
 * FORBIDDEN vs NOT_FOUND.
 *
 * When a caller lacks scope over a record we return 404, not 403 — a 403 would
 * confirm the record exists, which leaks the existence of another office's
 * correspondence to anyone probing identifiers (SEC-005, R-04).
 *
 * 403 is reserved for the case where the caller can already see the record but
 * lacks permission for the specific action.
 */
export const OUT_OF_SCOPE_RESPONSE_CODE: ErrorCode = 'NOT_FOUND';
